import React from "react";
import { Box, Button, IconButton, Link, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../../common/auth";
import { C } from "../../../common/theme";
import { AppHealth, AppHealthPoint, AppStatus, AppsHealthResponse, getAppsHealth } from "../../../common/requests/platform/endpoints";
import { Banner, LoadingRows, Panel, Section, StatusPill } from "../../../ui";
import { SLOW_SECONDS, STATUS_META, errorMessage, formatMs, formatUptime, formatWhen, useVisiblePolling } from "../platformData";

const POLL_MS = 60_000;
const SPARK_SLOTS = 60;
const SPARK_HEIGHT = 28;
const DAY_MS = 24 * 60 * 60 * 1000;

const gridSx = {
  display: "grid",
  gap: 1.25,
  // One column on phones; on wider screens as many ~240px panels as fit.
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(auto-fill, minmax(240px, 1fr))" },
};

export const spinSx = { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } };

export function StatusLabel({ status, detail }: { status: AppStatus; detail?: string | null }) {
  const meta = STATUS_META[status];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
      {detail && (
        <Typography variant="caption" noWrap title={detail} sx={{ minWidth: 0, color: C.muted }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}

function pointTitle(p: AppHealthPoint) {
  const when = formatWhen(p.checked_at);
  if (!p.ok) return `${when} · ${STATUS_META[p.status].label}`;
  return `${when} · ${formatMs(p.latency_ms)}${p.status === "degraded" ? " · slow" : ""}`;
}

/** One bar per check, newest on the right. Bar height is latency relative to
 * the slowest successful check shown; failed checks are full-height bars in
 * the status colour (with the count spelled out in the label). */
function LatencySparkline({ history }: { history: AppHealthPoint[] }) {
  const points = history.slice(-SPARK_SLOTS);
  if (!points.length) {
    return (
      <Box sx={{ height: SPARK_HEIGHT, display: "flex", alignItems: "center" }}>
        <Typography variant="caption" sx={{ color: C.muted }}>
          No history yet
        </Typography>
      </Box>
    );
  }
  const latencies = points.filter((p) => p.ok && p.latency_ms !== null).map((p) => p.latency_ms as number);
  const max = Math.max(1, ...latencies);
  const failed = points.filter((p) => !p.ok).length;
  const h = 24;
  return (
    <Box sx={{ color: C.line2, lineHeight: 0 }}>
      <svg
        width="100%"
        height={SPARK_HEIGHT}
        viewBox={`0 0 ${SPARK_SLOTS} ${h}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Response time of the last ${points.length} checks${failed ? `, ${failed} failed` : ", all succeeded"}`}
      >
        <line x1={0} x2={SPARK_SLOTS} y1={h - 0.25} y2={h - 0.25} stroke="currentColor" strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
        {points.map((p, i) => {
          const height = p.ok ? Math.max(1.5, ((p.latency_ms ?? 0) / max) * (h - 2)) : h;
          const color = p.ok && p.status === "healthy" ? C.ink2 : STATUS_META[p.status].color;
          return (
            <rect key={p.checked_at + i} x={SPARK_SLOTS - points.length + i + 0.15} y={h - height} width={0.7} height={height} fill={color}>
              <title>{pointTitle(p)}</title>
            </rect>
          );
        })}
      </svg>
    </Box>
  );
}

function AppPanel({ app }: { app: AppHealth }) {
  const detail =
    app.error ?? (app.status === "degraded" && app.ok ? `slower than ${SLOW_SECONDS}s` : app.status_code ? `HTTP ${app.status_code}` : null);
  return (
    <Panel padding={1.75} sx={{ display: "flex", flexDirection: "column", gap: 0.75, borderColor: app.status === "down" ? C.danger : C.line }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Link
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          underline="hover"
          variant="subtitle2"
          noWrap
          title={`${app.name} · ${app.url}`}
          sx={{ fontWeight: 700, minWidth: 0, flex: 1 }}
        >
          {app.name}
        </Link>
        <Typography variant="body2" className="num" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          {formatMs(app.latency_ms)}
        </Typography>
      </Box>
      <StatusLabel status={app.status} detail={detail} />
      <LatencySparkline history={app.history} />
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, justifyContent: "space-between" }}>
        <Tooltip title={`${app.checks_24h} check${app.checks_24h === 1 ? "" : "s"} in the window; latency averages successful checks only`}>
          <Typography variant="caption" sx={{ color: C.ink2, fontWeight: 600 }}>
            {app.uptime_24h === null ? "No uptime yet" : `${formatUptime(app.uptime_24h)} up · avg ${formatMs(app.avg_latency_ms)}`}
          </Typography>
        </Tooltip>
        <Typography variant="caption" sx={{ color: C.muted }}>
          Checked {formatWhen(app.checked_at)}
        </Typography>
      </Box>
    </Panel>
  );
}

function summaryText(summary: AppsHealthResponse["summary"]) {
  if (summary.total === 0) return "No apps configured";
  if (summary.issues === 0) return `All ${summary.total} healthy`;
  return [summary.down ? `${summary.down} down` : null, summary.degraded ? `${summary.degraded} degraded` : null, `${summary.healthy} healthy`]
    .filter(Boolean)
    .join(" · ");
}

function windowNote(historySince: string | null) {
  if (!historySince) return null;
  const since = new Date(historySince);
  if (Date.now() - since.getTime() >= DAY_MS) return "Uptime and averages cover the last 24 hours.";
  return `Uptime and averages cover checks since ${formatWhen(historySince)}, when the admin API last started (history is kept in memory, up to 24 hours).`;
}

export default function AppStatusSection() {
  const { idToken } = useAuth();
  const tokenRef = React.useRef(idToken);
  tokenRef.current = idToken;
  const [data, setData] = React.useState<AppsHealthResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const poll = React.useCallback(async (signal: AbortSignal, manual: boolean) => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const res = await getAppsHealth(token, { refresh: manual, signal });
      if (signal.aborted) return;
      setData(res.data);
      setError(null);
    } catch (e: any) {
      if (signal.aborted) return;
      setError(errorMessage(e, "Couldn't load app status"));
    }
  }, []);

  const { trigger, busy } = useVisiblePolling(!!idToken, POLL_MS, poll);

  const apps = React.useMemo(
    () =>
      data
        ? data.apps
            .map((app, index) => ({ app, index }))
            .sort((a, b) => STATUS_META[a.app.status].rank - STATUS_META[b.app.status].rank || a.index - b.index)
            .map(({ app }) => app)
        : [],
    [data]
  );

  const issues = data?.summary.issues ?? 0;

  return (
    <Section
      title="Apps"
      subtitle={
        <>
          {data && (
            <Box component="span" sx={{ fontWeight: issues ? 700 : 500, color: issues ? C.coralText : C.ink2 }}>
              {summaryText(data.summary)}
              {" · "}
            </Box>
          )}
          {data?.checked_at ? `Checked ${formatWhen(data.checked_at)}, ` : ""}every minute while this tab is open
        </>
      }
      actions={
        <Tooltip title="Check every app now">
          <span>
            <IconButton onClick={trigger} disabled={busy || !idToken} aria-label="Check every app now" sx={{ width: 44, height: 44 }}>
              <RefreshIcon fontSize="small" sx={busy ? spinSx : undefined} />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {error && (
        <Banner
          tone={data ? "warning" : "danger"}
          action={
            <Button variant="outlined" size="small" onClick={trigger} disabled={busy}>
              Retry
            </Button>
          }
        >
          {data ? `${error.replace(/\.$/, "")}. Showing the last check, from ${formatWhen(data.checked_at)}.` : error}
        </Banner>
      )}

      {data ? (
        <>
          <Box sx={{ ...gridSx, opacity: error ? 0.6 : 1 }}>
            {apps.map((app) => (
              <AppPanel key={app.key} app={app} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: C.muted }}>
            {windowNote(data.history_since)}
          </Typography>
        </>
      ) : !error ? (
        <Panel padding={0}>
          <LoadingRows rows={4} height={56} />
        </Panel>
      ) : null}
    </Section>
  );
}
